/**
 * Hooks pour l'espace parent : mes élèves et détail élève.
 */
import { useCallback } from "react";
import useSWR from "swr";
import { useAuthentication } from "./useAuthentication";
import {
  fetchMyStudents,
  fetchStudentActiveTrips,
  fetchStudentTripHistory,
  fetchStudentPayments,
  fetchStudentPresenceHistory,
  fetchStudentStatistics,
  updateStudentProfile,
  updateStudentAvailability,
  type MyStudent,
  type StudentProfileUpdatePayload,
  type StudentAvailabilityPayload,
} from "../services/tracking/studentsService";

const MY_STUDENTS_KEY = "tracking-my-students";

export function useMyStudents() {
  const { user } = useAuthentication();
  const token = user?.auth?.access_token;

  const { data: students, error, isLoading, mutate } = useSWR<MyStudent[]>(
    token ? [MY_STUDENTS_KEY, token] : null,
    () => fetchMyStudents(token),
    { revalidateOnFocus: false }
  );

  const refetch = useCallback(() => mutate(), [mutate]);

  const updateProfile = useCallback(
    async (studentId: number, payload: Partial<StudentProfileUpdatePayload>, method?: "PUT" | "PATCH") => {
      const updated = await updateStudentProfile(token, studentId, payload, method ?? "PATCH");
      await mutate();
      return updated;
    },
    [token, mutate]
  );

  const updateAvailability = useCallback(
    async (payload: StudentAvailabilityPayload) => {
      return updateStudentAvailability(token, payload);
    },
    [token]
  );

  return {
    students: students ?? [],
    isLoading,
    error,
    refetch,
    updateProfile,
    updateAvailability,
    accessToken: token,
  };
}

export function useStudentActiveTrips(studentId: number | null) {
  const { user } = useAuthentication();
  const token = user?.auth?.access_token;

  const { data: trips, isLoading, error, mutate } = useSWR(
    token && studentId ? ["tracking-student-active-trips", studentId, token] : null,
    () => fetchStudentActiveTrips(token!, studentId!),
    { revalidateOnFocus: false }
  );

  return {
    trips: Array.isArray(trips) ? trips : [],
    isLoading,
    error,
    refetch: mutate,
  };
}

export function useStudentTripHistory(studentId: number | null, pageSize = 20) {
  const { user } = useAuthentication();
  const token = user?.auth?.access_token;

  const { data, isLoading, error, mutate } = useSWR(
    token && studentId ? ["tracking-student-trip-history", studentId, pageSize, token] : null,
    () => fetchStudentTripHistory(token!, studentId!, { page_size: pageSize }),
    { revalidateOnFocus: false }
  );

  return {
    count: data?.count ?? 0,
    results: data?.results ?? [],
    isLoading,
    error,
    refetch: mutate,
  };
}

export function useStudentPayments(studentId: number | null, pageSize = 20) {
  const { user } = useAuthentication();
  const token = user?.auth?.access_token;

  const { data, isLoading, error, mutate } = useSWR(
    token && studentId ? ["tracking-student-payments", studentId, pageSize, token] : null,
    () => fetchStudentPayments(token!, studentId!, { page_size: pageSize }),
    { revalidateOnFocus: false }
  );

  return {
    count: data?.count ?? 0,
    results: data?.results ?? [],
    isLoading,
    error,
    refetch: mutate,
  };
}

export function useStudentPresenceHistory(studentId: number | null, pageSize = 20) {
  const { user } = useAuthentication();
  const token = user?.auth?.access_token;

  const { data, isLoading, error, mutate } = useSWR(
    token && studentId ? ["tracking-student-presence", studentId, pageSize, token] : null,
    () => fetchStudentPresenceHistory(token!, studentId!, { page_size: pageSize }),
    { revalidateOnFocus: false }
  );

  return {
    count: data?.count ?? 0,
    results: data?.results ?? [],
    isLoading,
    error,
    refetch: mutate,
  };
}

export function useStudentStatistics(studentId: number | null) {
  const { user } = useAuthentication();
  const token = user?.auth?.access_token;

  const { data: statistics, isLoading, error, mutate } = useSWR(
    token && studentId ? ["tracking-student-statistics", studentId, token] : null,
    () => fetchStudentStatistics(token!, studentId!),
    { revalidateOnFocus: false }
  );

  return {
    statistics: statistics ?? {},
    isLoading,
    error,
    refetch: mutate,
  };
}

export type { MyStudent, StudentProfileUpdatePayload, StudentAvailabilityPayload };
